import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import { withI18n } from '@lingui/react';
import { Trans } from '@lingui/macro';
import { prefix } from '../i18n-config';
import './CircularFloatingNav.css';
import Logo from './img/Logo';
import InstagramIcon from './img/InstagramIcon';
import TiktokIcon from './img/ThreadIcon';
import YoutubeIcon from './img/YoutubeIcon';

const CloseIcon = ({ style }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="#000" 
    strokeWidth="2.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    style={style}
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const CircularFloatingNav = ({ lang, pathName, onNewsletterClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConcertsOpen, setIsConcertsOpen] = useState(false);
  const [isAlbumsOpen, setIsAlbumsOpen] = useState(false);
  const [isDevLocalhost, setIsDevLocalhost] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [isStoreVisible, setIsStoreVisible] = useState(true);

  const trackEvent = (type, message) => {
    try {
      const apiUrl = process.env.GATSBY_API_URL || '';
      fetch(`${apiUrl}/v1/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, message }),
        keepalive: true,
      }).catch(e => console.error(e));
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('cfn-open');
    } else {
      document.body.classList.remove('cfn-open');
    }
    return () => {
      document.body.classList.remove('cfn-open');
    };
  }, [isOpen]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const isDev = process.env.NODE_ENV === 'development';
      setIsDevLocalhost(isDev && isLocal);
      
      const isStaging = window.location.hostname.includes('staging') || window.location.hostname === 'staging.adachan.com';
      setIsStoreVisible(isLocal || isStaging);
      
      const savedDebug = localStorage.getItem('ada_debug_mode') === 'true';
      setIsDebugMode(savedDebug);
    }
  }, []);


  const handleDebugToggle = () => {
    const nextState = !isDebugMode;
    setIsDebugMode(nextState);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('ada_debug_mode', String(nextState));
    }
    console.log(`[DEBUG MODE] Toggled to: ${nextState}`);
  };

  const langPrefix = prefix(lang);
  const getLangUrl = (targetLang) => {
    if (targetLang === lang) return '#';
    const search = typeof window !== 'undefined' ? window.location.search || '' : '';
    if (targetLang === 'fr') {
      return `/fr${pathName === '/' ? '' : pathName}${search}`;
    } else {
      const engPath = pathName.startsWith('/fr') ? pathName.substr(3) : pathName;
      const base = engPath || '/';
      return `${base}${search}`;
    }
  };

  const toggleOpen = () => {
    if (!isOpen) {
      trackEvent('nav_open', 'Circular Floating Navigation Opened');
      setIsOpen(true);
      setTimeout(() => {
        setIsConcertsOpen(true);
        setIsAlbumsOpen(true);
      }, 300);
    } else {
      setIsOpen(false);
      setIsConcertsOpen(false);
      setIsAlbumsOpen(false);
    }
  };

  const handleClick = (e, target, label) => {
    setIsOpen(false);
    
    if (target && pathName.includes('clandestine-melodies')) {
      const targetElem = document.querySelector(`#${target}`);
      if (targetElem) {
        e.preventDefault();
        targetElem.scrollIntoView({ behavior: 'smooth' });
      }
    }

    if (label) {
      trackEvent('nav_click', `Floating Menu -> ${label}`);
    }
  };

  if (pathName.includes('clandestine-melodies')) {
    return null;
  }

  return (
    <div className={`cfn-container ${isOpen ? 'open' : ''}`}>
      <button className="cfn-toggle-btn" onClick={toggleOpen}>
        <Logo style={{ 
          width: '32px', 
          height: 'auto', 
          position: 'absolute',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          opacity: isOpen ? 0 : 1,
          transform: isOpen ? 'rotate(90deg) scale(0)' : 'rotate(0) scale(1)'
        }} />
        <CloseIcon style={{ 
          width: '24px', 
          height: '24px', 
          position: 'absolute',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'rotate(0) scale(1)' : 'rotate(-90deg) scale(0)'
        }} />
      </button>

      <div className={`cfn-menu ${isOpen ? 'expanded' : ''}`}>
        <Link 
          to={`${langPrefix}/`} 
          className="cfn-menu-logo" 
          style={{ position: 'absolute', top: '22px', left: '20px', width: '80px' }}
          onClick={(e) => handleClick(e, null, 'Logo')}
        >
          <Logo className="main-header__logo" />
        </Link>
        <div className="cfn-menu-links">
          <a
            href="#"
            className="cfn-buy-now"
            onClick={(e) => {
              e.preventDefault();
              handleClick(e, null, 'Newsletter Sign-Up');
              if (onNewsletterClick) {
                onNewsletterClick();
              }
            }}
          >
            <Trans>Newsletter Sign-Up</Trans>
          </a>
          {isStoreVisible && (
            <Link
              to={`${langPrefix}/store`}
              onClick={(e) => handleClick(e, null, 'Store')}
            >
              <Trans>Store</Trans>
            </Link>
          )}



          <div className="cfn-submenu-wrapper">
            <div className="cfn-submenu-toggle" style={{ cursor: 'default' }}>
              <Trans>2026 Tour</Trans>
            </div>
            
            <div className={`cfn-subnav ${isConcertsOpen ? 'open' : ''}`}>
              <Link
                to={`${langPrefix}/the-letters-within/july-concert`}
                onClick={(e) => handleClick(e, null, 'Tour -> Next Chapters')}
              >
                <div className="cfn-subnav-header">
                  <span className="cfn-subnav-title"><Trans>Next Chapters</Trans></span>
                </div>
                <span className="cfn-subnav-subtitle"><Trans>July 4th</Trans></span>
              </Link>
              <Link
                to={`${langPrefix}/the-letters-within/live-in-concert`}
                onClick={(e) => handleClick(e, null, 'Tour -> Live in Concert')}
              >
                <span className="cfn-subnav-title"><Trans>Live in Concert</Trans></span>
                <span className="cfn-subnav-subtitle"><Trans>April 26th</Trans></span>
              </Link>
            </div>
          </div>


          <div className="cfn-submenu-wrapper">
            <div className="cfn-submenu-toggle" style={{ cursor: 'default' }}>
              <Trans>Albums</Trans>
            </div>
            
            <div className={`cfn-subnav ${isAlbumsOpen ? 'open' : ''}`}>
              <Link
                to={`${langPrefix}/albums/the-letters-within`}
                onClick={(e) => handleClick(e, null, 'Albums -> The Letters Within')}
              >
                <div className="cfn-subnav-header">
                  <span className="cfn-subnav-title"><Trans>The Letters Within</Trans></span>
                  <span className="cfn-new-tag"><Trans>NEW</Trans></span>
                </div>
                <span className="cfn-subnav-subtitle"><Trans>May 22nd, 2026</Trans></span>
              </Link>
              <Link
                to={`${langPrefix}/albums/clandestine-melodies`}
                onClick={(e) => handleClick(e, null, 'Albums -> Clandestine Melodies')}
              >
                <span className="cfn-subnav-title"><Trans>Clandestine Melodies</Trans></span>
                <span className="cfn-subnav-subtitle"><Trans>October 4th, 2024</Trans></span>
              </Link>
            </div>
          </div>
          
          <div className="cfn-social-links">
            <span className="cfn-social-label"><Trans>Social</Trans></span>
            <a href="https://www.instagram.com/adachan.music" target="_blank" rel="noopener noreferrer" className="cfn-social-icon" onClick={() => trackEvent('nav_click', 'Floating Menu -> Social -> Instagram')}>
              <InstagramIcon />
            </a>
            <a href="https://www.tiktok.com/@adachan.music" target="_blank" rel="noopener noreferrer" className="cfn-social-icon" onClick={() => trackEvent('nav_click', 'Floating Menu -> Social -> TikTok')}>
              <TiktokIcon />
            </a>
            <a href="https://www.youtube.com/@AdaTunes" target="_blank" rel="noopener noreferrer" className="cfn-social-icon" onClick={() => trackEvent('nav_click', 'Floating Menu -> Social -> YouTube')}>
              <YoutubeIcon />
            </a>
          </div>

          <div className="cfn-lang-toggle">
            <Link 
              to={getLangUrl('en')} 
              className={`cfn-lang-segment ${lang === 'en' ? 'active' : ''}`}
              onClick={(e) => { if (lang === 'en') e.preventDefault(); else handleClick(e, null, 'Language -> EN'); }}
            >
              EN
            </Link>
            <div className="cfn-lang-divider">|</div>
            <Link 
              to={getLangUrl('fr')} 
              className={`cfn-lang-segment ${lang === 'fr' ? 'active' : ''}`}
              onClick={(e) => { if (lang === 'fr') e.preventDefault(); else handleClick(e, null, 'Language -> FR'); }}
            >
              FR
            </Link>
          </div>

          {isDevLocalhost && (
            <div className="cfn-debug-section">
              <span className="cfn-debug-label"><Trans>Debug Mode</Trans></span>
              <button 
                className={`cfn-debug-toggle ${isDebugMode ? 'active' : ''}`} 
                onClick={handleDebugToggle}
              >
                {isDebugMode ? <Trans>ON</Trans> : <Trans>OFF</Trans>}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default withI18n()(CircularFloatingNav);
